/**
 * QuickScreener.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Rapid 10-activity screening tool (2 per Panchakosha domain).
 *
 * FIXES (this version):
 *  1. saveScreening: res.ok check + text fallback — surfaces real backend errors.
 *     Root cause of "next is not a function": add `next` param to backend route.
 *  2. Report now uses SRTReportPDF.generateQSReport — full report with skill
 *     clusters, activity suggestions, signal tables, NISH header/footer.
 *     Fixes 0% issue: overallScore field mapped correctly from computeResult.
 *  3. Slideshow / Exam mode with per-activity countdown timer.
 *     pill badges (✅ Green / ⚠️ Amber / 🔴 Red) visibly in the printed report.
 *  3. Slideshow / Exam mode: new 🎬 Exam tab — one activity per slide, prev/next
 *     navigation, hidden teacher-facing countdown timer per activity.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/auth/AuthContext";
import SRTHistoryPanel from "./SRTHistoryPanel";
import SRTInsightsPanel from "./SRTInsightsPanel";
// FIX 2: Rich report generator — skill clusters, activity suggestions, full signal tables
import { generateQSReport } from "./SRTReportPDF";
// FIX 2: Rich report generator — full skill clusters, activity suggestions, NISH header/footer

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── Inject fonts ──────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&family=Lora:ital,wght@0,600;0,700;1,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; }
  @keyframes qs-up    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes qs-in    { from{opacity:0;transform:scale(.97)} to{opacity:1;transform:scale(1)} }
  @keyframes qs-spin  { to{transform:rotate(360deg)} }
  @keyframes qs-pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
  @keyframes qs-slide { from{transform:translateX(-8px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes qs-slideleft  { from{transform:translateX(32px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes qs-slideright { from{transform:translateX(-32px);opacity:0} to{transform:translateX(0);opacity:1} }
  .qs-up        { animation: qs-up .45s ease both; }
  .qs-in        { animation: qs-in .25s ease both; }
  .qs-spin      { animation: qs-spin .9s linear infinite; display:inline-block; }
  .qs-slideleft  { animation: qs-slideleft .3s cubic-bezier(.22,.68,0,1.2) both; }
  .qs-slideright { animation: qs-slideright .3s cubic-bezier(.22,.68,0,1.2) both; }
  .qs-hover { transition: transform .15s, box-shadow .15s; }
  .qs-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,.12) !important; }
  @keyframes qs-timer-warn { 0%,100%{background:rgba(192,57,43,.08)} 50%{background:rgba(192,57,43,.2)} }
  .qs-timer-warn { animation: qs-timer-warn 1s ease infinite; }
`;
function injectCSS() {
  if (document.getElementById("qs-css")) return;
  const s = document.createElement("style");
  s.id = "qs-css"; s.textContent = CSS;
  document.head.appendChild(s);
}

// ─── Palette ───────────────────────────────────────────────────────────────
const P = {
  navy: "#0B1F35", ink: "#1A2B3C", muted: "#637080", border: "#E0E8F0",
  fog: "#F4F7FA", white: "#FFFFFF",
  green: "#1A7A40", greenBg: "#E8F5EE",
  amber: "#B06000", amberBg: "#FEF3E2",
  red: "#C0392B", redBg: "#FDECEA",
  sky: "#1A6FAA",
};

// ─── Domain definitions ────────────────────────────────────────────────────
const DOMAINS = [
  { id: "annamaya",    label: "Annamaya",    full: "Annamaya Kosha",    subtitle: "Physical & Motor",               icon: "🏃", color: "#0E8A7A", bg: "#E6F5F3", dark: "#0A6E62" },
  { id: "pranamaya",   label: "Pranamaya",   full: "Pranamaya Kosha",   subtitle: "Socio-Emotional",                icon: "💛", color: "#C2620A", bg: "#FEF0E6", dark: "#A0520A" },
  { id: "manomaya",    label: "Manomaya",    full: "Manomaya Kosha",    subtitle: "Mind & Cognition",               icon: "🧠", color: "#6E3EA6", bg: "#F3EEF9", dark: "#5A318A" },
  { id: "vijnanamaya", label: "Vijnanamaya", full: "Vijnanamaya Kosha", subtitle: "Language, Literacy & Numeracy",  icon: "📚", color: "#1A4F7A", bg: "#EBF2FA", dark: "#153E60" },
  { id: "anandamaya",  label: "Anandamaya",  full: "Anandamaya Kosha",  subtitle: "Joy, Play & Creativity",         icon: "🎨", color: "#B03060", bg: "#FCE8F0", dark: "#8A1E48" },
];

// ─── THE 10 ACTIVITIES ─────────────────────────────────────────────────────
const ACTIVITIES = [
  {
    id: "an-1", domain: "annamaya", seq: 1, title: "The Drawing Challenge",
    duration: "2 min", icon: "✏️",
    tagline: "Draw yourself, then trace this path ⭐ ——→ 🏠",
    setup: "Place a sheet of paper with a dotted star-to-house path on one side. Give the child a crayon.",
    instruction: "Say to the child: 'Draw a picture of yourself on this side. Then follow the dotted path on the other side with your finger or crayon.'",
    what_to_watch: "Observe grip, pressure, posture, eye-hand coordination, figure completeness, and line-following accuracy simultaneously.",
    signals: [
      { id: "an1-grip",    label: "Pencil/crayon grip",            hint: "Mature tripod vs palmar/fist grasp",                            weight: 3, srtItems: ["an01","an11"] },
      { id: "an1-figure",  label: "Person drawing complexity",     hint: "Scribble only / head-only / 2-3 parts / full figure",           weight: 3, srtItems: ["an10","an16"] },
      { id: "an1-trace",   label: "Can follow a dotted path",      hint: "Able to track and follow a visual line",                        weight: 2, srtItems: ["an02","an16"] },
      { id: "an1-posture", label: "Appropriate seated posture",    hint: "Core stability, bilateral coordination, trunk control",         weight: 1, srtItems: ["an09"] },
      { id: "an1-shapes",  label: "Attempts or copies any shapes", hint: "Does the drawing contain intentional closed forms",             weight: 2, srtItems: ["an16","an11"] },
    ],
  },
  {
    id: "an-2", domain: "annamaya", seq: 2, title: "Move Like Me",
    duration: "2 min", icon: "🤸",
    tagline: "Jump, balance, clap-pattern, throw to hoop",
    setup: "Clear a small floor space. Place a hoop or chalk circle ~1.5m away. Have a soft ball or beanbag ready.",
    instruction: "Say: 'Let's do some fun moves together! Jump on both feet — now balance on one foot — now copy my clap: clap… clap-clap… can you throw the ball into that circle?'",
    what_to_watch: "Four micro-tasks in one sequence. Each is observable in ~20 seconds. Note which the child attempts, avoids, or succeeds at.",
    signals: [
      { id: "an2-jump",   label: "Jumps with both feet & lands",       hint: "Both feet leave ground; landing is controlled or unsteady",    weight: 3, srtItems: ["an05","an09"] },
      { id: "an2-balance",label: "Balances on one foot briefly",        hint: "Even 2–3 seconds counts; note if refused",                    weight: 2, srtItems: ["an09","an05"] },
      { id: "an2-rhythm", label: "Copies clap rhythm pattern",          hint: "AB clap pattern; exact match vs approximation vs no response", weight: 2, srtItems: ["an17"] },
      { id: "an2-throw",  label: "Throws with directional aim",         hint: "Any throw toward target; doesn't need to land in hoop",       weight: 2, srtItems: ["an15","an08"] },
      { id: "an2-engage", label: "Engages willingly or needs coaxing",  hint: "Willingness to participate in physical activity",             weight: 1, srtItems: ["pr12","pr13"] },
    ],
  },
  {
    id: "pr-1", domain: "pranamaya", seq: 3, title: "The Feelings Puppet Show",
    duration: "2 min", icon: "🎭",
    tagline: "Puppet shows emotions — child responds, identifies, acts",
    setup: "Use any small puppet or soft toy. Have 3 emotion picture cards (happy, sad, scared) visible.",
    instruction: "Make the puppet show 3 emotions one at a time: happy (clap & smile), sad (hang head), scared (cover eyes). Ask: 'How does the puppet feel now? How do YOU feel when you're scared?' Then ask child to make the puppet show happy.",
    what_to_watch: "Watch for emotion recognition, labelling, empathic response, communication mode, and willingness to play-act.",
    signals: [
      { id: "pr1-recog",   label: "Identifies 2+ basic emotions",       hint: "Happy, sad, scared — verbal, pointing, or AAC",              weight: 3, srtItems: ["pr01","pr03","pr07"] },
      { id: "pr1-label",   label: "Labels emotion with a word/sign",    hint: "Uses any communication mode to name the emotion",            weight: 3, srtItems: ["pr01","pr07"] },
      { id: "pr1-empathy", label: "Shows empathic response to puppet",  hint: "Leans in, says 'poor puppet', offers comfort",               weight: 2, srtItems: ["pr08","pr04"] },
      { id: "pr1-pretend", label: "Makes puppet express an emotion",    hint: "Any pretend/symbolic puppet use",                            weight: 2, srtItems: ["pr16","aa14"] },
      { id: "pr1-express", label: "Expresses own feeling verbally",     hint: "Names or shows how they feel in a similar scenario",         weight: 2, srtItems: ["pr10","pr18"] },
    ],
  },
  {
    id: "pr-2", domain: "pranamaya", seq: 4, title: "The Sharing Game",
    duration: "2 min", icon: "🤝",
    tagline: "Turn-taking ball roll, sticker sharing, waiting in line",
    setup: "A soft ball and a sheet of stickers. Two chairs facing each other ~1.5m apart.",
    instruction: "Roll the ball back and forth 4 times ('your turn… my turn…'). Then say: 'I have stickers — let's share them with everyone.' Finally: 'Let's line up for our next activity — wait for me to say go.'",
    what_to_watch: "Three sub-tasks test turn-taking, generosity, and impulse control in a natural play context.",
    signals: [
      { id: "pr2-turns",  label: "Waits for their turn (ball roll)",           hint: "No reminders / 1 reminder / constant reminders / refuses",          weight: 3, srtItems: ["pr02","pr09"] },
      { id: "pr2-share",  label: "Shares stickers without resistance",         hint: "Willing / brief hesitation / refuses / only under pressure",          weight: 3, srtItems: ["pr05"] },
      { id: "pr2-wait",   label: "Waits in line calmly",                       hint: "Stays in place without bolting or constant fidgeting",               weight: 2, srtItems: ["pr09","pr11"] },
      { id: "pr2-rules",  label: "Follows 2-step game instruction",            hint: "Sit → roll; wait → go — follows both parts",                         weight: 2, srtItems: ["pr11","mn15"] },
      { id: "pr2-repair", label: "Responds if ball rolls past them",           hint: "Retrieves and returns (cooperative); ignores (avoidant)",            weight: 1, srtItems: ["pr14","pr15"] },
    ],
  },
  {
    id: "mn-1", domain: "manomaya", seq: 5, title: "The Sorting Tray",
    duration: "2 min", icon: "🗂️",
    tagline: "Sort animals/vehicles, extend a pattern, find the odd one",
    setup: "6 picture cards (3 animals, 3 vehicles) mixed in a pile. A row of 4 coloured blocks: Red Blue Red Blue. 4 more picture cards for the odd-one-out (3 fruit + 1 bus).",
    instruction: "Show the pile: 'Sort these into animals and vehicles.' Then show the block row: 'What colour block comes next?' Then show 4 cards: 'Three go together — which one is different?'",
    what_to_watch: "Three sub-tasks in 2 minutes. Sorting shows classification; pattern continuation shows sequencing; odd-one-out shows logical grouping.",
    signals: [
      { id: "mn1-sort",    label: "Correctly sorts all into 2 groups", hint: "All 6 correct / 4-5 / 2-3 / random",                         weight: 3, srtItems: ["mn03","mn14"] },
      { id: "mn1-pattern", label: "Extends AB colour pattern",         hint: "Says or places Blue; needs prompt; incorrect; no attempt",    weight: 3, srtItems: ["mn02","mn06"] },
      { id: "mn1-oddone",  label: "Identifies the odd one out",        hint: "Picks bus (non-fruit); can explain why",                     weight: 3, srtItems: ["mn01","mn10"] },
      { id: "mn1-focus",   label: "Sustains focus across all 3 tasks", hint: "Completes all / needs redirection / only 1-2 tasks",         weight: 2, srtItems: ["mn12","pr11"] },
      { id: "mn1-explain", label: "Gives a reason for their choice",   hint: "'Because it's not food' — any verbal justification",         weight: 2, srtItems: ["mn01","mn11"] },
    ],
  },
  {
    id: "mn-2", domain: "manomaya", seq: 6, title: "The Memory Sequence",
    duration: "2 min", icon: "🧩",
    tagline: "3-item recall, 3-step instruction, story retell (3 events)",
    setup: "3 small objects (e.g. toy car, pencil, spoon). A simple 3-sentence story written on a card for the teacher to read aloud.",
    instruction: "Show 3 objects for 10 sec, hide them: 'What did you see?' Then give 3-step instruction: 'Stand up, walk to the door, come back and sit.' Then read: 'Riya woke up. She ate breakfast. She went to school.' Ask: 'Tell me what happened.'",
    what_to_watch: "Three memory/sequence tasks back-to-back. Together they reveal working memory, instruction-following depth, and narrative sequencing.",
    signals: [
      { id: "mn2-recall",   label: "Recalls 2+ of 3 hidden objects",        hint: "3/3 strong; 2/3 developing; 0-1 emerging",                      weight: 3, srtItems: ["mn04","mn16"] },
      { id: "mn2-3step",    label: "Follows all 3-step instruction",         hint: "All 3 / first 2 / first 1 / no follow",                         weight: 3, srtItems: ["mn15","pr11"] },
      { id: "mn2-retell",   label: "Retells story in correct order",         hint: "All 3 events / 2 events / 1 event / no recall",                 weight: 3, srtItems: ["mn18","mn09"] },
      { id: "mn2-sequence", label: "Uses sequence words (first/then)",       hint: "'First she woke up, then…' — any ordering language",            weight: 2, srtItems: ["mn09","vj15"] },
      { id: "mn2-attend",   label: "Listens to story without prompting",     hint: "Attends to 3 sentences without redirection",                    weight: 1, srtItems: ["mn12","vj12"] },
    ],
  },
  {
    id: "vj-1", domain: "vijnanamaya", seq: 7, title: "The Number & Letter Hunt",
    duration: "2 min", icon: "🔢",
    tagline: "Count stars, bigger number, letter hunt, first sound",
    setup: "A card with ⭐⭐⭐⭐ (4 stars). A card showing '6' and '3'. A card with letters A B M S T. Say the word 'SUN' clearly.",
    instruction: "'Count these stars and tap the number card (3/4/5/6).' Then: 'Which number is bigger — 6 or 3?' Then: 'Point to the letter A… now M… now S.' Then: 'What's the FIRST sound in the word… SUN?'",
    what_to_watch: "Four numeracy/literacy micro-tasks. Together they span counting, quantity comparison, letter recognition, and phonological awareness.",
    signals: [
      { id: "vj1-count",  label: "Counts 4 objects correctly",        hint: "Points and counts 1-to-1; identifies correct numeral card",   weight: 3, srtItems: ["vj03","vj07"] },
      { id: "vj1-bigger", label: "Identifies bigger of two numbers",  hint: "6 > 3 — correct / incorrect / no response",                  weight: 3, srtItems: ["vj04","vj14"] },
      { id: "vj1-letters",label: "Recognises 3+ printed letters",     hint: "Points correctly to A, M, S from a set of 5",                weight: 3, srtItems: ["vj05","vj02"] },
      { id: "vj1-sound",  label: "Identifies first sound of 'SUN'",  hint: "/s/ — correct / approximate / whole word / no response",     weight: 3, srtItems: ["vj11","vj06"] },
      { id: "vj1-1to1",   label: "1-to-1 counting correspondence",    hint: "Touches each star once while counting — no skipping",        weight: 2, srtItems: ["vj03","vj07"] },
    ],
  },
  {
    id: "vj-2", domain: "vijnanamaya", seq: 8, title: "The Story Picture",
    duration: "2 min", icon: "📖",
    tagline: "Describe a scene, make a sentence, write/spell own name",
    setup: "A simple scene picture (home/park/school — any familiar setting with people doing things). A blank paper and crayon for name writing.",
    instruction: "'Look at this picture — tell me EVERYTHING you see.' Then: 'Make a sentence using the word 'school'.' Then: 'Can you write your name? Or spell it out loud?'",
    what_to_watch: "Language depth, vocabulary range, sentence construction, and early literacy — all from one natural scene description task.",
    signals: [
      { id: "vj2-describe", label: "Describes full scene (not just labels)", hint: "Narrative / partial scene / isolated labels / no response",   weight: 3, srtItems: ["vj01","mn11"] },
      { id: "vj2-sentence", label: "Forms a grammatical sentence",           hint: "Full sentence / 2-word phrase / single word / no attempt",   weight: 3, srtItems: ["vj16","pr10"] },
      { id: "vj2-vocab",    label: "Uses varied vocabulary (5+ words)",      hint: "Count distinct meaningful words in the description",          weight: 2, srtItems: ["vj01","vj16"] },
      { id: "vj2-name",     label: "Writes or spells own name",              hint: "Legible / recognisable attempt / partial / scribble / cannot",weight: 3, srtItems: ["vj18","vj09"] },
      { id: "vj2-ordinal",  label: "Uses any position/sequence language",    hint: "'The boy is next to…', 'first there's a…'",                  weight: 1, srtItems: ["vj17","an12"] },
    ],
  },
  {
    id: "aa-1", domain: "anandamaya", seq: 9, title: "The Creation Station",
    duration: "2 min", icon: "🎨",
    tagline: "Free draw anything, design a flag, describe it",
    setup: "Blank paper, crayons in 4+ colours. No instructions on what to draw.",
    instruction: "'Draw anything you want — it can be real or made-up. There's no wrong answer!' After 90 seconds: 'Now design your own flag using colours and shapes.' Then: 'Tell me about what you made.'",
    what_to_watch: "Observe creative intent, colour use, motor control, imaginative engagement, and verbal description quality.",
    signals: [
      { id: "aa1-intent",   label: "Draws with clear creative intent",         hint: "Random marks / purposeful scribble / recognisable content",  weight: 3, srtItems: ["aa02","aa12"] },
      { id: "aa1-explains", label: "Describes what they made",                 hint: "Detailed description / brief / names only / no response",    weight: 3, srtItems: ["aa02","aa13"] },
      { id: "aa1-colour",   label: "Chooses and uses colours deliberately",    hint: "Selects specific colours for purpose (not random)",           weight: 2, srtItems: ["aa01","aa09"] },
      { id: "aa1-flag",     label: "Creates a distinct flag design",           hint: "Any intentional design vs random marks",                     weight: 2, srtItems: ["aa12","aa02"] },
      { id: "aa1-engage",   label: "Shows visible joy or absorption",          hint: "Smiling, humming, fully absorbed in creative task",           weight: 2, srtItems: ["aa16","pr17"] },
    ],
  },
  {
    id: "aa-2", domain: "anandamaya", seq: 10, title: "The Imagination Round",
    duration: "2 min", icon: "🚀",
    tagline: "Favourite animal + story, sing/hum, invent something",
    setup: "No materials needed. This is a conversational + movement activity.",
    instruction: "'What's your favourite animal? Tell me one thing about it and do its movement!' Then hum or clap a simple melody and invite them to join. Finally: 'If you could invent ANYTHING, what would you make?'",
    what_to_watch: "Three tiny tasks reveal self-expression, musical responsiveness, body representation, and imaginative fluency.",
    signals: [
      { id: "aa2-animal", label: "Names animal + shares a fact or feeling", hint: "Name+fact+emotion / name+fact / name only / no response", weight: 3, srtItems: ["aa05","pr18"] },
      { id: "aa2-move",   label: "Imitates the animal's movement",          hint: "Full-body / partial / gesture / refused",                 weight: 2, srtItems: ["aa11","an05"] },
      { id: "aa2-music",  label: "Joins in with music (hum/clap)",          hint: "Sings/hums / claps rhythm / slight response / no response",weight: 2, srtItems: ["aa07","an17"] },
      { id: "aa2-invent", label: "Describes an imaginary invention",        hint: "Detailed / brief / single word / no response",            weight: 3, srtItems: ["aa18","aa13"] },
      { id: "aa2-story",  label: "Adds any story/narrative to animal",      hint: "'My elephant loves to swim and…' — any extension",       weight: 2, srtItems: ["aa03","aa13"] },
    ],
  },
];

const SIGNAL_LEVELS = [
  { id: "strong",   label: "Strong",   short: "✓✓", desc: "Clear, independent",       score: 2,  color: "#1A7A40", bg: "#E8F5EE" },
  { id: "present",  label: "Present",  short: "✓",  desc: "With minor support",       score: 1,  color: "#1A6FAA", bg: "#EBF4FA" },
  { id: "emerging", label: "Emerging", short: "~",  desc: "Attempted, inconsistent",  score: 0,  color: "#B06000", bg: "#FEF3E2" },
  { id: "absent",   label: "Absent",   short: "✗",  desc: "Not observed / refused",   score: -1, color: "#C0392B", bg: "#FDECEA" },
];

// ─── Scoring ───────────────────────────────────────────────────────────────
function computeResult(ratings) {
  const domainScores  = {};
  const domainAssessed = {};

  DOMAINS.forEach(d => {
    const acts = ACTIVITIES.filter(a => a.domain === d.id);
    let total = 0, maxTotal = 0, anyRated = false;
    acts.forEach(act => {
      act.signals.forEach(sig => {
        const lv = ratings[sig.id];
        if (lv) {
          anyRated = true;
          const lvDef = SIGNAL_LEVELS.find(l => l.id === lv);
          total += sig.weight * (lvDef?.score ?? 0);
          maxTotal += sig.weight * 2;
        } else {
          maxTotal += sig.weight * 2;
        }
      });
    });
    domainAssessed[d.id] = anyRated;
    if (!anyRated) {
      domainScores[d.id] = null;
      return;
    }
    const shifted = total + maxTotal;
    const maxShifted = 2 * maxTotal;
    domainScores[d.id] = maxShifted > 0 ? Math.round((shifted / maxShifted) * 100) : 50;
  });

  const assessedScores = DOMAINS.map(d => domainScores[d.id]).filter(s => s !== null);
  const overall = assessedScores.length > 0
    ? Math.round(assessedScores.reduce((a, b) => a + b, 0) / assessedScores.length)
    : 0;

  const flags = {};
  DOMAINS.forEach(d => {
    if (!domainAssessed[d.id]) {
      flags[d.id] = "none";
      return;
    }
    const s = domainScores[d.id];
    flags[d.id] = s >= 65 ? "green" : s >= 42 ? "amber" : "red";
  });

  const redCount   = Object.values(flags).filter(f => f === "red").length;
  const amberCount = Object.values(flags).filter(f => f === "amber").length;

  let recommendation;
  if (redCount >= 2)                          recommendation = "full-srt";
  else if (redCount === 1 || amberCount >= 3) recommendation = "monitor";
  else                                         recommendation = "no-concerns";

  return { domainScores, overall, flags, recommendation, domainAssessed };
}

// ─── Parse duration string → seconds ──────────────────────────────────────
function parseDurationSecs(str = "2 min") {
  const m = str.match(/(\d+)/);
  return m ? parseInt(m[1], 10) * 60 : 120;
}

// ─── Print report (FIX 2: visible coloured flag badges in printed output) ──
// printScreeningReport and internal generateQSReport removed.
// Using generateQSReport imported from SRTReportPDF.js instead.
// This gives the full rich report: skill clusters, home activities,
// NISH header/footer, all 50 signals, domain flag badges.

// ─── Tiny helpers ──────────────────────────────────────────────────────────
function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((new Date() - new Date(dob)) / 31557600000);
}

function flagColor(fl) {
  return fl === "green" ? P.green : fl === "amber" ? P.amber : fl === "red" ? P.red : P.muted;
}
function flagBg(fl) {
  return fl === "green" ? P.greenBg : fl === "amber" ? P.amberBg : fl === "red" ? P.redBg : P.fog;
}

// ─── SignalRow ─────────────────────────────────────────────────────────────
function SignalRow({ signal, value, onChange }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${P.border}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: P.ink, marginBottom: 2 }}>
            {signal.label}
            <span style={{ marginLeft: 6, fontSize: 10, background: "#E8EFF6", color: P.muted, padding: "1px 6px", borderRadius: 4 }}>×{signal.weight}</span>
          </div>
          <div style={{ fontSize: 11, color: P.muted, fontStyle: "italic" }}>{signal.hint}</div>
          <div style={{ fontSize: 10, color: "#A0B0BC", marginTop: 3 }}>SRT: {signal.srtItems.join(", ")}</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
        {SIGNAL_LEVELS.map(lv => {
          const sel = value === lv.id;
          return (
            <button key={lv.id} onClick={() => onChange(sel ? null : lv.id)}
              style={{
                padding: "8px 4px", border: `2px solid ${sel ? lv.color : P.border}`,
                borderRadius: 9, background: sel ? lv.bg : P.white,
                cursor: "pointer", textAlign: "center", transition: "all .15s",
                fontFamily: "Outfit, sans-serif"
              }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: sel ? lv.color : P.muted }}>{lv.short}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: sel ? lv.color : P.muted, marginTop: 1 }}>{lv.label}</div>
              <div style={{ fontSize: 8, color: sel ? lv.color : "#B0BEC5", marginTop: 1, lineHeight: 1.3 }}>{lv.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── ActivityCard (list mode) ──────────────────────────────────────────────
function ActivityCard({ activity, ratings, onRate, isActive, onToggle, completed }) {
  const domain = DOMAINS.find(d => d.id === activity.domain);
  const rated  = activity.signals.filter(s => ratings[s.id]).length;
  const total  = activity.signals.length;

  return (
    <div style={{
      background: P.white, borderRadius: 14,
      border: isActive ? `2px solid ${domain.color}` : `1.5px solid ${P.border}`,
      boxShadow: isActive ? `0 6px 28px ${domain.color}18` : "0 1px 5px rgba(0,0,0,.05)",
      overflow: "hidden", transition: "box-shadow .2s, border-color .2s"
    }}>
      <div onClick={onToggle}
        style={{
          padding: "14px 18px", cursor: "pointer",
          background: isActive ? domain.bg : P.white,
          borderBottom: isActive ? `1px solid ${domain.color}18` : "none",
          display: "flex", alignItems: "center", gap: 12
        }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: domain.bg,
          border: `2px solid ${domain.color}33`, display: "flex",
          alignItems: "center", justifyContent: "center",
          flexShrink: 0, position: "relative"
        }}>
          <span style={{ fontSize: 18 }}>{activity.icon}</span>
          {completed && (
            <div style={{
              position: "absolute", top: -5, right: -5, width: 16, height: 16,
              background: P.green, borderRadius: "50%", display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: 9, color: "white", fontWeight: 900
            }}>✓</div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: domain.color, background: domain.bg, padding: "1px 7px", borderRadius: 5, border: `1px solid ${domain.color}22` }}>
              {domain.icon} {domain.label}
            </span>
            <span style={{ fontSize: 9, color: P.muted, background: P.fog, padding: "1px 6px", borderRadius: 5 }}>⏱ {activity.duration}</span>
          </div>
          <div style={{ fontWeight: 800, fontSize: 14, color: isActive ? domain.dark : P.ink }}>
            {activity.seq}. {activity.title}
          </div>
          <div style={{ fontSize: 11, color: P.muted, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {activity.tagline}
          </div>
        </div>
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: rated === total ? P.green : rated > 0 ? P.amber : P.muted }}>{rated}/{total}</div>
          <div style={{ fontSize: 9, color: P.muted }}>signals</div>
        </div>
        <div style={{ fontSize: 11, color: P.muted, flexShrink: 0, transform: isActive ? "rotate(180deg)" : "none", transition: "transform .2s" }}>▾</div>
      </div>

      {isActive && (
        <div className="qs-in" style={{ padding: "16px 20px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ background: "#F8FAFC", border: `1px solid ${P.border}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 6 }}>🔧 SETUP</div>
              <div style={{ fontSize: 12, color: P.ink, lineHeight: 1.7 }}>{activity.setup}</div>
            </div>
            <div style={{ background: domain.bg, border: `1px solid ${domain.color}22`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: domain.color, letterSpacing: .5, marginBottom: 6 }}>💬 SAY TO CHILD</div>
              <div style={{ fontSize: 12, color: domain.dark, lineHeight: 1.7, fontStyle: "italic" }}>"{activity.instruction}"</div>
            </div>
          </div>
          <div style={{ background: "#FFFBEA", border: "1px solid #E8C84A", borderRadius: 9, padding: "9px 14px", marginBottom: 16, display: "flex", gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>👁️</span>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#7A6000", letterSpacing: .5, marginBottom: 3 }}>WHAT TO WATCH</div>
              <div style={{ fontSize: 12, color: "#5A4800", lineHeight: 1.6 }}>{activity.what_to_watch}</div>
            </div>
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 4 }}>RATE EACH SIGNAL</div>
          {activity.signals.map(sig => (
            <SignalRow key={sig.id} signal={sig} value={ratings[sig.id] || null} onChange={val => onRate(sig.id, val)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SlideshowView — FIX 3: Exam / Slideshow mode ─────────────────────────
function SlideshowView({ ratings, onRate, onExit, onSave, saving, child }) {
  const [idx,         setIdx]        = useState(0);
  const [direction,   setDirection]  = useState("left"); // for slide animation
  const [animKey,     setAnimKey]    = useState(0);
  const [timerSecs,   setTimerSecs]  = useState(() => parseDurationSecs(ACTIVITIES[0].duration));
  const [timerActive, setTimerActive]= useState(false);
  const [timerExpired,setTimerExpired]= useState(false);
  const intervalRef = useRef(null);


  const act    = ACTIVITIES[idx];
  const domain = DOMAINS.find(d => d.id === act.domain);
  const rated  = act.signals.filter(s => ratings[s.id]).length;
  const total  = act.signals.length;
  const isLast = idx === ACTIVITIES.length - 1;

  // Reset timer when activity changes
  useEffect(() => {
    clearInterval(intervalRef.current);
    setTimerSecs(parseDurationSecs(act.duration));
    setTimerActive(false);
    setTimerExpired(false);
  }, [idx]);

  // Countdown
  useEffect(() => {
    if (!timerActive) { clearInterval(intervalRef.current); return; }
    intervalRef.current = setInterval(() => {
      setTimerSecs(s => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setTimerActive(false);
          setTimerExpired(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [timerActive]);

  function navigate(dir) {
    setDirection(dir === 1 ? "left" : "right");
    setAnimKey(k => k + 1);
    setIdx(i => Math.max(0, Math.min(ACTIVITIES.length - 1, i + dir)));
  }

  const mins = Math.floor(timerSecs / 60);
  const secs = timerSecs % 60;
  const timerStr = `${mins}:${String(secs).padStart(2, "0")}`;
  const timerPct = (timerSecs / parseDurationSecs(act.duration)) * 100;
  const timerColor = timerSecs > 30 ? "#1A7A40" : timerSecs > 10 ? P.amber : P.red;

  const totalSignals  = ACTIVITIES.reduce((s, a) => s + a.signals.length, 0);
  const ratedSignals  = Object.values(ratings).filter(Boolean).length;
  const completedActs = ACTIVITIES.filter(a => a.signals.every(s => ratings[s.id])).length;

  return (
    <div style={{
      position: "fixed", inset: 0, background: P.navy, zIndex: 200,
      display: "flex", flexDirection: "column", fontFamily: "Outfit, sans-serif"
    }}>
      {/* ── Slideshow topbar ── */}
      <div style={{
        height: 48, background: "rgba(0,0,0,.3)", display: "flex",
        alignItems: "center", padding: "0 16px", gap: 12, flexShrink: 0,
        borderBottom: "1px solid rgba(255,255,255,.08)"
      }}>
        <button onClick={onExit} style={{
          background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(255,255,255,.2)",
          borderRadius: 7, padding: "4px 12px", fontSize: 11, cursor: "pointer",
          fontFamily: "Outfit, sans-serif", fontWeight: 700
        }}>✕ Exit</button>

        {/* Progress pills */}
        <div style={{ display: "flex", gap: 4, flex: 1, overflow: "hidden" }}>
          {ACTIVITIES.map((a, i) => {
            const done = a.signals.every(s => ratings[s.id]);
            const dom  = DOMAINS.find(d => d.id === a.domain);
            return (
              <button key={a.id} onClick={() => { setDirection(i > idx ? "left" : "right"); setAnimKey(k=>k+1); setIdx(i); }}
                style={{
                  flex: 1, height: 5, border: "none", cursor: "pointer",
                  borderRadius: 3,
                  background: i === idx
                    ? dom.color
                    : done
                    ? dom.color + "99"
                    : "rgba(255,255,255,.15)",
                  transition: "background .2s"
                }} />
            );
          })}
        </div>

        {/* Activity counter */}
        <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,.7)", flexShrink: 0 }}>
          {idx + 1} <span style={{ color: "rgba(255,255,255,.35)" }}>/ {ACTIVITIES.length}</span>
        </span>

        {/* Hidden teacher timer — small, unobtrusive, NOT shown to child in normal screen position */}
        <div style={{
          display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
          background: timerExpired ? "rgba(192,57,43,.25)" : "rgba(255,255,255,.06)",
          border: `1px solid ${timerExpired ? P.red + "66" : "rgba(255,255,255,.12)"}`,
          borderRadius: 8, padding: "4px 10px"
        }} className={timerExpired ? "qs-timer-warn" : ""}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,.4)", fontWeight: 700 }}>⏱ TEACHER</span>
          <span style={{ fontSize: 15, fontWeight: 900, color: timerColor, fontFamily: "monospace", letterSpacing: 1 }}>
            {timerStr}
          </span>
          <button onClick={() => timerSecs > 0 && setTimerActive(a => !a)}
            style={{
              background: timerActive ? P.amber + "44" : P.green + "44",
              border: "none", borderRadius: 5, padding: "2px 7px",
              fontSize: 9, fontWeight: 800,
              color: timerActive ? P.amber : P.green,
              cursor: "pointer", fontFamily: "Outfit, sans-serif"
            }}>
            {timerActive ? "⏸" : timerExpired ? "✓" : "▶"}
          </button>
        </div>

        {/* Save button */}
        <button onClick={onSave} disabled={saving || ratedSignals === 0} style={{
          background: (saving || ratedSignals === 0) ? "rgba(255,255,255,.08)" : "#F5D000",
          color: (saving || ratedSignals === 0) ? "rgba(255,255,255,.3)" : P.navy,
          border: "none", borderRadius: 7, padding: "4px 14px",
          fontWeight: 800, fontSize: 11, cursor: "pointer",
          fontFamily: "Outfit, sans-serif", flexShrink: 0
        }}>
          {saving ? "Saving…" : "💾 Save"}
        </button>
      </div>

      {/* Timer bar */}
      <div style={{ height: 3, background: "rgba(255,255,255,.06)", flexShrink: 0 }}>
        <div style={{
          height: "100%", background: timerColor,
          width: timerActive || timerExpired ? `${timerPct}%` : "0%",
          transition: "width 1s linear, background .5s",
          borderRadius: "0 2px 2px 0"
        }} />
      </div>

      {/* ── Main slide area ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 100px", minHeight: 0 }}>
        <div key={animKey} className={direction === "left" ? "qs-slideleft" : "qs-slideright"}
          style={{ maxWidth: 720, margin: "0 auto" }}>

          {/* Domain + activity header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14, marginBottom: 16,
            background: "rgba(255,255,255,.05)", borderRadius: 14, padding: "14px 18px",
            border: `1px solid ${domain.color}44`
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 13, background: domain.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 26, flexShrink: 0, border: `2px solid ${domain.color}55`
            }}>{act.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ background: domain.bg, color: domain.color, fontSize: 10, fontWeight: 800, padding: "2px 9px", borderRadius: 6 }}>
                  {domain.icon} {domain.full}
                </span>
                <span style={{ background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.5)", fontSize: 10, padding: "2px 9px", borderRadius: 6 }}>
                  ⏱ {act.duration}
                </span>
                <span style={{
                  background: rated === total ? "#1A7A4033" : rated > 0 ? "#B0600033" : "rgba(255,255,255,.05)",
                  color: rated === total ? "#7FD8A0" : rated > 0 ? "#FFD080" : "rgba(255,255,255,.3)",
                  fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 6
                }}>{rated}/{total} signals</span>
              </div>
              <div style={{ fontWeight: 900, fontSize: 20, color: "white", fontFamily: "Lora, serif" }}>
                {act.seq}. {act.title}
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 3 }}>{act.tagline}</div>
            </div>
          </div>

          {/* Setup + Instruction 2-col */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,.4)", letterSpacing: .5, marginBottom: 8 }}>🔧 SETUP</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.8)", lineHeight: 1.7 }}>{act.setup}</div>
            </div>
            <div style={{ background: `${domain.color}18`, border: `1px solid ${domain.color}44`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: domain.color, letterSpacing: .5, marginBottom: 8 }}>💬 SAY TO CHILD</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,.9)", lineHeight: 1.7, fontStyle: "italic" }}>
                "{act.instruction}"
              </div>
            </div>
          </div>

          {/* What to watch */}
          <div style={{ background: "#2D2400", border: "1px solid #E8C84A33", borderRadius: 10, padding: "11px 16px", marginBottom: 14, display: "flex", gap: 10 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>👁️</span>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#E8C84A", letterSpacing: .5, marginBottom: 4 }}>WHAT TO WATCH</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.75)", lineHeight: 1.7 }}>{act.what_to_watch}</div>
            </div>
          </div>

          {/* Signal ratings */}
          <div style={{ background: "white", borderRadius: 14, padding: "16px 20px" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 4 }}>RATE EACH SIGNAL</div>
            {act.signals.map(sig => (
              <SignalRow key={sig.id} signal={sig} value={ratings[sig.id] || null} onChange={val => onRate(sig.id, val)} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom nav bar ── */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, height: 68,
        background: "rgba(11,31,53,.96)", borderTop: "1px solid rgba(255,255,255,.1)",
        display: "flex", alignItems: "center", padding: "0 20px", gap: 14,
        backdropFilter: "blur(12px)"
      }}>
        <button onClick={() => navigate(-1)} disabled={idx === 0}
          style={{
            background: idx > 0 ? "rgba(255,255,255,.1)" : "rgba(255,255,255,.04)",
            color: idx > 0 ? "white" : "rgba(255,255,255,.2)",
            border: "1px solid rgba(255,255,255,.1)", borderRadius: 10,
            padding: "10px 20px", fontSize: 13, fontWeight: 800,
            cursor: idx > 0 ? "pointer" : "not-allowed",
            fontFamily: "Outfit, sans-serif", flexShrink: 0
          }}>← Prev</button>

        {/* Domain dots */}
        <div style={{ flex: 1, display: "flex", justifyContent: "center", gap: 8 }}>
          {DOMAINS.map(d => {
            const acts  = ACTIVITIES.filter(a => a.domain === d.id);
            const done  = acts.filter(a => a.signals.every(s => ratings[s.id])).length;
            const isCur = ACTIVITIES[idx].domain === d.id;
            return (
              <div key={d.id} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 14 }}>{d.icon}</div>
                <div style={{ height: 4, width: 28, borderRadius: 3, marginTop: 3,
                  background: done === acts.length ? d.color : isCur ? d.color + "88" : "rgba(255,255,255,.15)" }} />
                <div style={{ fontSize: 8, color: "rgba(255,255,255,.4)", marginTop: 2 }}>{done}/{acts.length}</div>
              </div>
            );
          })}
        </div>

        {/* Overall completion */}
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: "white" }}>{completedActs}/10</div>
          <div style={{ fontSize: 8, color: "rgba(255,255,255,.4)" }}>done</div>
        </div>

        {isLast ? (
          <button onClick={onExit}
            style={{
              background: "#F5D000", color: P.navy, border: "none", borderRadius: 10,
              padding: "10px 24px", fontSize: 13, fontWeight: 900,
              cursor: "pointer", fontFamily: "Outfit, sans-serif", flexShrink: 0
            }}>📊 View Result →</button>
        ) : (
          <button onClick={() => navigate(1)}
            style={{
              background: domain.color, color: "white", border: "none", borderRadius: 10,
              padding: "10px 24px", fontSize: 13, fontWeight: 800,
              cursor: "pointer", fontFamily: "Outfit, sans-serif", flexShrink: 0
            }}>Next → {ACTIVITIES[idx + 1]?.title}</button>
        )}
      </div>
    </div>
  );
}

// ─── DomainBadge ───────────────────────────────────────────────────────────
function DomainBadge({ domain, score, flag, size = "md" }) {
  const fc = flagColor(flag); const fb = flagBg(flag);
  const isLg = size === "lg";
  const notAssessed = flag === "none" || score == null;
  return (
    <div style={{ background: fb, border: `1.5px solid ${fc}33`, borderRadius: isLg ? 14 : 10, padding: isLg ? "14px 18px" : "8px 12px", textAlign: "center" }}>
      <div style={{ fontSize: isLg ? 28 : 18 }}>{domain.icon}</div>
      <div style={{ fontWeight: 900, fontSize: isLg ? 22 : 14, color: fc, fontFamily: "Lora, serif", lineHeight: 1 }}>{notAssessed ? "—" : `${score}%`}</div>
      <div style={{ fontSize: isLg ? 11 : 9, color: fc, fontWeight: 700, marginTop: 2 }}>{domain.label}</div>
      {flag && (
        <div style={{ fontSize: isLg ? 10 : 8, marginTop: 4, background: fc, color: "white", borderRadius: 8, padding: "2px 6px", fontWeight: 800 }}>
          {flag === "green" ? "✅ OK" : flag === "amber" ? "⚠️ Monitor" : flag === "red" ? "🔴 Refer" : "— Not Assessed"}
        </div>
      )}
    </div>
  );
}

// ─── ResultScreen ──────────────────────────────────────────────────────────
function ResultScreen({ child, ratings, date, onBack, onRestart }) {
  const result = computeResult(ratings);
  const { domainScores, overall, flags, recommendation } = result;

  const REC_CONFIG = {
    "no-concerns": { icon:"✅", label:"No Concerns",          color:P.green, bg:P.greenBg,
      headline:"Child is developing well across all five Panchakosha dimensions.",
      action:"No immediate intervention needed. Continue enrichment activities and re-screen in 6 months.", next:"Re-screen in 6 months" },
    "monitor":     { icon:"⚠️", label:"Monitor & Support",    color:P.amber, bg:P.amberBg,
      headline:"Emerging skills observed in one or more domains.",
      action:"Provide targeted classroom support. Re-screen in 8–10 weeks. If no progress, conduct full 90-item Panchakosha SRT.", next:"Re-screen in 8–10 weeks" },
    "full-srt":    { icon:"🔴", label:"Full SRT Recommended", color:P.red,   bg:P.redBg,
      headline:"Significantly delayed skills in 2+ Panchakosha domains.",
      action:"Conduct the full 90-item Panchakosha SRT. Share results with a qualified special educator for an Individualised Education Plan.", next:"Launch Full SRT" },
  };
  const rec = REC_CONFIG[recommendation];
  const ratedCount = Object.values(ratings).filter(Boolean).length;

  return (
    <div className="qs-up" style={{ padding: "0 0 80px" }}>
      <div style={{ background: `linear-gradient(135deg, ${P.navy} 0%, #1A3A5C 100%)`, padding: "32px 28px", marginBottom: 24, position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.4)", letterSpacing: 1.5, marginBottom: 16 }}>PANCHAKOSHA QUICK SCREENER — RESULT</div>
          <div style={{ background: rec.bg, borderRadius: 14, padding: "18px 22px", border: `2px solid ${rec.color}44`, marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 42, flexShrink: 0 }}>{rec.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: 22, color: rec.color, fontFamily: "Lora, serif", marginBottom: 4 }}>{rec.label}</div>
              <div style={{ fontSize: 13, color: P.ink, lineHeight: 1.6, fontWeight: 500 }}>{rec.headline}</div>
            </div>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 44, fontWeight: 900, color: rec.color, fontFamily: "Lora, serif", lineHeight: 1 }}>{overall}%</div>
              <div style={{ fontSize: 10, color: P.muted, fontWeight: 600 }}>overall</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
            {DOMAINS.map(d => <DomainBadge key={d.id} domain={d} score={domainScores[d.id]} flag={flags[d.id]} size="lg" />)}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: "0 auto 20px", padding: "0 20px" }}>
        <div style={{ background: P.white, borderRadius: 14, padding: "20px 22px", border: `1.5px solid ${rec.color}33`, boxShadow: `0 4px 20px ${rec.color}12`, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: rec.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📋</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: P.ink, marginBottom: 6 }}>Recommended Next Step</div>
              <div style={{ fontSize: 13, color: P.ink, lineHeight: 1.8 }}>{rec.action}</div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {recommendation === "full-srt" && (
                  <button onClick={onRestart} style={{ padding: "9px 20px", background: P.navy, color: "white", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>🎮 Launch Full SRT →</button>
                )}
                <button onClick={() => generateQSReport({ ratings, domainScores, overallScore: overall, recommendation, flags, date, notes: null }, child || {})}
                  style={{ padding: "9px 20px", background: rec.bg, color: rec.color, border: `1.5px solid ${rec.color}44`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                  🖨️ Download Report
                </button>
                <button onClick={onBack} style={{ padding: "9px 16px", background: P.fog, color: P.muted, border: `1.5px solid ${P.border}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                  ← Edit Ratings
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Meta row */}
        <div style={{ background: P.fog, borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "flex", gap: 16, flexWrap: "wrap", border: `1px solid ${P.border}`, fontSize: 12 }}>
          {[
            ["Child", child?.name || "–"],
            ["Age", child?.dob ? `Age ${calcAge(child.dob)}` : "–"],
            ["Date", date],
            ["Signals Rated", `${ratedCount} of ${ACTIVITIES.reduce((s, a) => s + a.signals.length, 0)}`],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 6 }}>
              <span style={{ color: P.muted, fontWeight: 600 }}>{k}:</span>
              <span style={{ fontWeight: 700, color: P.ink }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Per-domain detail */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: P.ink, marginBottom: 12 }}>Domain Detail</div>
          {DOMAINS.map(d => {
            const sc = domainScores[d.id]; const fl = flags[d.id];
            const fc = flagColor(fl); const fb = flagBg(fl);
            const acts = ACTIVITIES.filter(a => a.domain === d.id);
            return (
              <div key={d.id} style={{ background: P.white, borderRadius: 12, marginBottom: 10, border: `1.5px solid ${P.border}`, overflow: "hidden" }}>
                <div style={{ background: fb, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${fc}22` }}>
                  <span style={{ fontSize: 20 }}>{d.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: d.dark }}>{d.full}</div>
                    <div style={{ fontSize: 10, color: d.color }}>{d.subtitle}</div>
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 20, color: fc, fontFamily: "Lora, serif" }}>{sc}%</div>
                  <div style={{ background: fc, color: "white", fontSize: 10, fontWeight: 700, padding: "3px 12px", borderRadius: 10 }}>
                    {fl === "green" ? "✅ No concerns" : fl === "amber" ? "⚠️ Monitor" : "🔴 Refer"}
                  </div>
                </div>
                <div style={{ padding: "8px 16px" }}>
                  {acts.flatMap(act => act.signals.map(sig => {
                    const lv = SIGNAL_LEVELS.find(l => l.id === ratings[sig.id]);
                    return (
                      <div key={sig.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 0", borderBottom: `1px solid ${P.border}`, fontSize: 11 }}>
                        <span style={{ color: P.muted, fontSize: 9, minWidth: 80, fontWeight: 600 }}>{act.title}</span>
                        <span style={{ flex: 1, color: P.ink }}>{sig.label}</span>
                        {lv ? (
                          <span style={{ background: lv.bg, color: lv.color, fontSize: 9, fontWeight: 700, padding: "1px 8px", borderRadius: 6 }}>{lv.label}</span>
                        ) : (
                          <span style={{ color: "#C0CDD8", fontSize: 9 }}>—</span>
                        )}
                      </div>
                    );
                  }))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#637080", background: "#FEF3E2", border: "1px solid #F0C070", borderRadius: 8, padding: "8px 12px", marginBottom: 14 }}>
            ⚡ <strong>Quick Screener note:</strong> Skill cluster item scores are not available from a quick screening. Conduct the full 90-item SRT for detailed cluster analysis.
          </div>
          <SRTInsightsPanel
            responses={{}} koshaScores={domainScores} overallScore={overall}
            childName={child?.name || "This child"}
            childAge={child?.dob ? Math.floor((new Date() - new Date(child.dob)) / 31557600000) : null}
            disabilities={child?.disabilities || []}
            KOSHAS={DOMAINS}
          />
        </div>

        <div style={{ background: "#FFFBEA", border: "1.5px solid #E8C84A", borderRadius: 10, padding: 14, marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#B08000", marginBottom: 5 }}>📌 Important Notice</div>
          <p style={{ fontSize: 11, lineHeight: 1.8, color: "#555", margin: 0 }}>
            This quick screener is <strong>not a diagnostic tool</strong>. "Full SRT Recommended" should be followed by the 90-item Panchakosha SRT conducted by a qualified educator.
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function QuickScreener() {
  useEffect(() => { injectCSS(); }, []);
  const { getAuthHeader } = useAuth();

  const [tab,           setTab]          = useState("overview");
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [children,      setChildren]     = useState([]);
  const [childId,       setChildId]      = useState(null);
  const [date,          setDate]         = useState(new Date().toISOString().split("T")[0]);
  const [loadingKids,   setLoadingKids]  = useState(true);
  const [activeAct,     setActiveAct]    = useState("an-1");
  const [ratings,       setRatings]      = useState({});
  const [saving,        setSaving]       = useState(false);
  const [toast,         setToast]        = useState("");
  const [search,        setSearch]       = useState("");
  const [qsHistory,     setQsHistory]    = useState([]);
  const [loadingHist,   setLoadingHist]  = useState(false);
  const [activeResult,  setActiveResult] = useState(null);
  const [currentScreenerId, setCurrentScreenerId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const showToast = (msg, ms = 3500) => { setToast(msg); setTimeout(() => setToast(""), ms); };

  useEffect(() => {
    (async () => {
      setLoadingKids(true);
      try {
        const res  = await fetch(`${API}/children?limit=100`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success && data.children?.length) {
          setChildren(data.children);
          setChildId(data.children[0]._id);
        }
      } catch {}
      setLoadingKids(false);
    })();
  }, []);

  const child = children.find(c => c._id === childId) || null;

  useEffect(() => {
    if (!childId) return;
    setLoadingHist(true);
    setQsHistory([]);
    setActiveResult(null);
    setCurrentScreenerId(null);
    fetch(`${API}/srt-screener/${childId}?limit=20`, { headers: getAuthHeader() })
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setQsHistory(d.results || []);
          if (d.results?.length) setActiveResult(d.results[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingHist(false));
  }, [childId]);

  const rateSignal = useCallback((signalId, levelId) => {
    setRatings(prev => ({ ...prev, [signalId]: levelId }));
  }, []);

  const startNewScreening = useCallback(() => {
    setRatings({});
    setCurrentScreenerId(null);
  }, []);

  const totalSignals   = ACTIVITIES.reduce((s, a) => s + a.signals.length, 0);
  const ratedSignals   = Object.values(ratings).filter(Boolean).length;
  const pctDone        = Math.round((ratedSignals / totalSignals) * 100);
  const actComplete    = act => act.signals.every(s => ratings[s.id]);
  const completedCount = ACTIVITIES.filter(actComplete).length;
  const liveResult     = ratedSignals > 0 ? computeResult(ratings) : null;

  const loadQSResult = useCallback((result) => {
    if (result.ratings) {
      setRatings(result.ratings);
      setCurrentScreenerId(result._id || null);
      if (result.date) setDate(result.date.split("T")[0]);
      setActiveAct("an-1");
      setTab("screen");
      showToast(`📂 Loaded from ${new Date(result.date || result.createdAt).toLocaleDateString("en-IN")}`);
    }
  }, []);

  // ── FIX 1: saveScreening with proper error handling ──────────────────────
  // NOTE: If you still see "next is not a function" in your backend logs,
  // fix the Express route: change `async (req, res) =>` to `async (req, res, next) =>`
  // in your /api/srt-screener POST handler.
  const saveScreening = useCallback(async () => {
    if (!childId || ratedSignals === 0) return;
    setSaving(true);
    try {
      const result = computeResult(ratings);
      const isUpdate = Boolean(currentScreenerId);
      const url    = isUpdate ? `${API}/srt-screener/${currentScreenerId}` : `${API}/srt-screener`;
      const method = isUpdate ? "PUT" : "POST";
  
      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({
          childId, date, ratings,
          domainScores:   result.domainScores,
          flags:          result.flags,
          overallScore:   result.overall,
          recommendation: result.recommendation,
        }),
      });
  
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errData = await res.json();
          errMsg = errData.message || errMsg;
        } catch {
          const errText = await res.text().catch(() => "");
          errMsg = errText.split("\n")[0]?.slice(0, 120) || errMsg;
        }
        showToast(`⚠️ ${errMsg}`);
        return;
      }
  
      const data = await res.json();
      if (data.success) {
        showToast(isUpdate ? "✅ Screening updated" : "✅ Screening saved");
        if (data.screener?._id) setCurrentScreenerId(data.screener._id);
        const r2 = await fetch(`${API}/srt-screener/${childId}?limit=20`, { headers: getAuthHeader() });
        const d2 = await r2.json();
        if (d2.success) {
          setQsHistory(d2.results || []);
          if (d2.results?.length) setActiveResult(d2.results[0]);
        }
      } else {
        showToast(`⚠️ ${data.message || "Save failed"}`);
      }
    } catch (err) {
      showToast(`⚠️ ${err.message || "Network error"}`);
    } finally {
      setSaving(false);
    }
  }, [childId, date, ratings, currentScreenerId]);

  // Inline result content for overview tab
  const ResultContent = ({ result, forChild, forDate }) => {
    const { domainScores, flags, recommendation } = result;
    const overall = result.overall ?? result.overallScore ?? 0;
    const REC_CONFIG = {
      "no-concerns": { icon:"✅", label:"No Concerns",          color:P.green, bg:P.greenBg,
        headline:"Child is developing well across all five Panchakosha dimensions.",
        action:"No immediate intervention needed. Continue enrichment activities and re-screen in 6 months." },
      "monitor":     { icon:"⚠️", label:"Monitor & Support",    color:P.amber, bg:P.amberBg,
        headline:"Emerging skills observed in one or more domains.",
        action:"Provide targeted classroom support. Re-screen in 8–10 weeks. Full SRT recommended if no progress." },
      "full-srt":    { icon:"🔴", label:"Full SRT Recommended", color:P.red,   bg:P.redBg,
        headline:"Significantly delayed skills in 2+ Panchakosha domains.",
        action:"Conduct the full 90-item Panchakosha SRT and share with a qualified special educator for an IEP." },
    };
    const rec = REC_CONFIG[recommendation] || REC_CONFIG["monitor"];

    return (
      <div>
        <div style={{ background:rec.bg, border:`2px solid ${rec.color}33`, borderRadius:14, padding:"18px 22px", marginBottom:16, display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ fontSize:40, flexShrink:0 }}>{rec.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:900, fontSize:20, color:rec.color, fontFamily:"Lora, serif", marginBottom:4 }}>{rec.label}</div>
            <div style={{ fontSize:12, color:P.ink, lineHeight:1.7 }}>{rec.headline}</div>
            <div style={{ fontSize:12, color:"#555", marginTop:6, lineHeight:1.7 }}>{rec.action}</div>
          </div>
          <div style={{ textAlign:"center", flexShrink:0 }}>
            <div style={{ fontSize:42, fontWeight:900, color:rec.color, fontFamily:"Lora, serif", lineHeight:1 }}>{overall}%</div>
            <div style={{ fontSize:10, color:P.muted, fontWeight:600 }}>overall</div>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:18 }}>
          {DOMAINS.map(d => {
            const fl = flags[d.id] || "amber";
            const fc = flagColor(fl); const fb = flagBg(fl);
            const notAssessed = fl === "none" || domainScores[d.id] == null;
            const sc = notAssessed ? 0 : domainScores[d.id];
            return (
              <div key={d.id} style={{ background:fb, borderRadius:12, padding:"12px 8px", textAlign:"center", border:`1.5px solid ${fc}33`, borderTop:`3px solid ${fc}` }}>
                <div style={{ fontSize:20, marginBottom:4 }}>{d.icon}</div>
                <div style={{ fontSize:20, fontWeight:900, color:fc, fontFamily:"Lora, serif", lineHeight:1 }}>{notAssessed ? "—" : `${sc}%`}</div>
                <div style={{ fontSize:9, color:fc, fontWeight:700, marginTop:2 }}>{d.label}</div>
                <div style={{ height:4, background:"rgba(0,0,0,.08)", borderRadius:3, marginTop:6, overflow:"hidden" }}>
                  <div style={{ width:`${sc}%`, height:"100%", background:fc, borderRadius:3 }}/>
                </div>
                <div style={{ background:fc, color:"white", fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:8, marginTop:4, display:"inline-block" }}>
                  {fl==="green"?"✅ OK":fl==="amber"?"⚠️ Monitor":fl==="red"?"🔴 Refer":"— Not Assessed"}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom:18 }}>
          <div style={{ fontWeight:800, fontSize:13, color:P.ink, marginBottom:10 }}>Signal Breakdown</div>
          {DOMAINS.map(d => {
            const fl = flags[d.id] || "amber";
            const fc = flagColor(fl); const fb = flagBg(fl);
            const notAssessed = fl === "none" || domainScores[d.id] == null;
            const sc = notAssessed ? 0 : domainScores[d.id];
            const acts = ACTIVITIES.filter(a => a.domain === d.id);
            const sessionRatings = result.ratings || ratings;
            return (
              <div key={d.id} style={{ background:"white", borderRadius:12, marginBottom:8, border:`1.5px solid ${P.border}`, overflow:"hidden" }}>
                <div style={{ background:fb, padding:"9px 16px", display:"flex", alignItems:"center", gap:10, borderBottom:`1px solid ${fc}22` }}>
                  <span style={{ fontSize:18 }}>{d.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:800, fontSize:13, color:d.dark }}>{d.full}</div>
                    <div style={{ fontSize:10, color:d.color }}>{d.subtitle}</div>
                  </div>
                  <div style={{ fontWeight:900, fontSize:18, color:fc, fontFamily:"Lora, serif" }}>{notAssessed ? "—" : `${sc}%`}</div>
                  <div style={{ background:fc, color:"white", fontSize:9, fontWeight:800, padding:"3px 10px", borderRadius:10 }}>
                    {fl==="green"?"✅ No concerns":fl==="amber"?"⚠️ Monitor":fl==="red"?"🔴 Refer":"— Not Assessed"}
                  </div>
                </div>
                <div style={{ padding:"6px 16px" }}>
                  {acts.flatMap(act => act.signals.map(sig => {
                    const lv = SIGNAL_LEVELS.find(l => l.id === sessionRatings[sig.id]);
                    return (
                      <div key={sig.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"4px 0", borderBottom:`1px solid ${P.border}`, fontSize:11 }}>
                        <span style={{ color:P.muted, fontSize:9, minWidth:80, fontWeight:600 }}>{act.title}</span>
                        <span style={{ flex:1, color:P.ink }}>{sig.label}</span>
                        {lv
                          ? <span style={{ background:lv.bg, color:lv.color, fontSize:9, fontWeight:700, padding:"1px 8px", borderRadius:6 }}>{lv.label}</span>
                          : <span style={{ color:"#C0CDD8", fontSize:9 }}>—</span>
                        }
                      </div>
                    );
                  }))}
                </div>
              </div>
            );
          })}
        </div>

        <SRTInsightsPanel
  mode="quick"
  responses={{}} koshaScores={domainScores} overallScore={overall}
          childName={forChild?.name || "This child"}
          childAge={forChild?.dob ? Math.floor((new Date()-new Date(forChild.dob))/31557600000) : null}
          disabilities={forChild?.disabilities || []}
          KOSHAS={DOMAINS}
        />

        <div style={{ background:"#FFFBEA", border:"1.5px solid #E8C84A", borderRadius:10, padding:14, marginTop:14 }}>
          <div style={{ fontSize:11, fontWeight:800, color:"#B08000", marginBottom:5 }}>📌 Important Notice</div>
          <p style={{ fontSize:11, lineHeight:1.8, color:"#555", margin:0 }}>
            This quick screener is <strong>not a diagnostic tool</strong>. A "Full SRT Recommended" result should be followed by the 90-item Panchakosha SRT conducted by a qualified educator.
          </p>
        </div>

        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:16 }}>
          <button onClick={() => generateQSReport(
            { ...result, overallScore: result.overall ?? result.overallScore ?? 0, ratings: result.ratings||ratings, date: forDate||date, notes:null },
            forChild || child || {}
          )} style={{ padding:"9px 20px", background:rec.bg, color:rec.color, border:`1.5px solid ${rec.color}44`, borderRadius:9, fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"Outfit, sans-serif" }}>
            🖨️ Download Report
          </button>
          {recommendation === "full-srt" && (
            <button onClick={() => alert("Navigate to Full SRT")}
              style={{ padding:"9px 20px", background:P.navy, color:"white", border:"none", borderRadius:9, fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"Outfit, sans-serif" }}>
              🎮 Launch Full SRT →
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Slideshow / Exam mode overlay ──────────────────────────────────────
  if (showSlideshow) {
    return (
      <SlideshowView
        ratings={ratings}
        onRate={rateSignal}
        onExit={() => { setShowSlideshow(false); setTab("overview"); }}
        onSave={saveScreening}
        saving={saving}
        child={child}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // TWO-PANEL LAYOUT
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: "Outfit, sans-serif", display: "flex", flexDirection: "column",
      height: "100vh", overflow: "hidden", background: P.fog }}>

      {/* ── TOPBAR ── */}
      <div style={{ background: P.navy, color: "white", padding: "0 20px", height: 50,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 14px rgba(0,0,0,.25)", flexShrink: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ background: "white", color: P.navy, fontWeight: 900, fontSize: 11, padding: "2px 8px", borderRadius: 4 }}>NISH</div>
          <div style={{ width: 1, height: 18, background: "rgba(255,255,255,.2)" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,.8)" }}>FSLP</span>
          <div style={{ background: "rgba(255,255,255,.1)", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 14, color: "white" }}>
            ⚡ Quick Screener
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {toast && (
            <span className="qs-in" style={{ fontSize: 11, fontWeight: 700,
              color: toast.startsWith("✅") || toast.startsWith("📂") ? "#7FD8A0" : "#FFB3A7",
              padding: "3px 10px", background: "rgba(255,255,255,.08)", borderRadius: 8 }}>
              {toast}
            </span>
          )}
          {tab === "screen" && (
            <button onClick={() => setShowSlideshow(true)} style={{
              background: "#F5D000", color: P.navy, border: "none", borderRadius: 7,
              padding: "4px 14px", fontWeight: 800, fontSize: 11, cursor: "pointer",
              fontFamily: "Outfit, sans-serif" }}>
              🎬 Exam Mode
            </button>
          )}
          <div style={{ background: "#b30000", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 3 }}>NCAHT</div>
          <div style={{ background: "#006400", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 3 }}>ICMR</div>
        </div>
      </div>

      {/* ── BODY: sidebar + main ── */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: sidebarCollapsed ? "64px 1fr" : "280px 1fr",
        minHeight: 0, overflow: "hidden", transition: "grid-template-columns .18s ease" }}>

        {/* ════ SIDEBAR ════ */}
        <div style={{ background: "white", borderRight: `1.5px solid ${P.border}`,
          display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Header + collapse toggle */}
          <div style={{ padding: sidebarCollapsed ? "14px 0 10px" : "14px 16px 10px",
            borderBottom: `1px solid ${P.border}`, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: sidebarCollapsed ? "center" : "space-between" }}>
            {!sidebarCollapsed && <div style={{ fontWeight: 800, fontSize: 13, color: P.navy }}>Children</div>}
            <button onClick={() => setSidebarCollapsed(s => !s)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{ background: P.fog, border: `1px solid ${P.border}`, borderRadius: 7, width: 24, height: 24,
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                color: P.muted, fontSize: 11, flexShrink: 0 }}>
              {sidebarCollapsed ? "›" : "‹"}
            </button>
          </div>

          {/* Search */}
          {!sidebarCollapsed && (
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${P.border}`, flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
                  fontSize: 12, color: P.muted, pointerEvents: "none" }}>🔍</span>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search name or disability…"
                  style={{ width: "100%", padding: "7px 9px 7px 28px", border: `1.5px solid ${P.border}`,
                    borderRadius: 8, fontSize: 11, outline: "none", color: P.ink,
                    fontFamily: "Outfit, sans-serif", background: P.fog, boxSizing: "border-box" }} />
              </div>
            </div>
          )}

          {/* Child list */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loadingKids && (
              <div style={{ padding: 24, textAlign: "center", color: P.muted, fontSize: 11 }}>
                <span className="qs-spin">⏳</span> {!sidebarCollapsed && "Loading…"}
              </div>
            )}
            {children.filter(c =>
              c.name.toLowerCase().includes(search.toLowerCase()) ||
              (c.disabilities || []).some(d => d.toLowerCase().includes(search.toLowerCase()))
            ).map(c => {
              const sel = childId === c._id;
              const age = calcAge(c.dob);
              const disab = (c.disabilities || [])[0];
              const lastResult = sel && qsHistory.length > 0 ? qsHistory[0] : null;
              const lastPct = lastResult?.overallScore ?? 0;
              const lastFlagSet = lastResult?.flags || {};
              const lastRecIcon = lastResult?.recommendation === "full-srt" ? "🔴"
                : lastResult?.recommendation === "monitor" ? "⚠️"
                : lastResult ? "✅" : null;

              if (sidebarCollapsed) {
                return (
                  <div key={c._id} onClick={() => { setChildId(c._id); if (tab === "screen") setTab("overview"); }}
                    title={c.name}
                    style={{ padding: "8px 0", cursor: "pointer", display: "flex", justifyContent: "center",
                      borderBottom: `1px solid ${P.border}` }}>
                    <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                      background: c.color || P.sky,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "white", fontWeight: 900, fontSize: 14,
                      boxShadow: sel ? `0 0 0 2px ${c.color || P.sky}` : "none" }}>
                      {c.name[0]}
                    </div>
                  </div>
                );
              }

              return (
                <div key={c._id} onClick={() => { setChildId(c._id); if (tab === "screen") setTab("overview"); }}
                  style={{ padding: "10px 16px", cursor: "pointer",
                    background: sel ? `${c.color || P.sky}10` : "transparent",
                    borderLeft: sel ? `4px solid ${c.color || P.sky}` : "4px solid transparent",
                    borderBottom: `1px solid ${P.border}`, transition: "background .1s",
                    display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                    background: c.color || P.sky,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", fontWeight: 900, fontSize: 15 }}>
                    {c.name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 12,
                      color: sel ? (c.color || P.sky) : P.ink,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 10, color: P.muted, marginTop: 1 }}>
                      {age != null ? `Age ${age}` : ""}
                      {age != null && disab ? " · " : ""}
                      {disab || ""}
                    </div>
                  </div>
                  {lastRecIcon && (
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 16, lineHeight: 1 }}>{lastRecIcon}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: P.muted }}>{lastPct}%</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Date picker at bottom */}
          {!sidebarCollapsed && (
            <div style={{ padding: "12px 16px", borderTop: `1.5px solid ${P.border}`, flexShrink: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 5 }}>SCREENING DATE</div>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                style={{ width: "100%", border: `1.5px solid ${P.border}`, borderRadius: 7,
                  padding: "7px 10px", fontSize: 11, outline: "none", color: P.ink,
                  fontFamily: "Outfit, sans-serif", background: "white", boxSizing: "border-box" }} />
            </div>
          )}
        </div>

        {/* ════ MAIN PANEL ════ */}
        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>

          {/* Tab bar */}
          <div style={{ background: "white", borderBottom: `1.5px solid ${P.border}`,
            padding: "0 20px", display: "flex", alignItems: "center", flexShrink: 0 }}>
            {[
              ["overview", "📊 Overview"],
              ["history",  "📈 History"],
              ["screen",   "🎮 Screen"],
            ].map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                background: "none", border: "none", padding: "14px 16px 12px", fontSize: 13, fontWeight: 700,
                color: tab === t ? P.navy : P.muted,
                borderBottom: `3px solid ${tab === t ? P.sky : "transparent"}`,
                cursor: "pointer", fontFamily: "Outfit, sans-serif", transition: "color .15s",
                flexShrink: 0 }}>{l}</button>
            ))}
            <div style={{ flex: 1 }} />
            {tab === "screen" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                <div style={{ width: 70, height: 5, background: P.border, borderRadius: 5, overflow: "hidden" }}>
                  <div style={{ width: `${pctDone}%`, height: "100%", background: P.sky, borderRadius: 5, transition: "width .4s" }} />
                </div>
                <span style={{ fontWeight: 800, color: P.sky }}>{ratedSignals}</span>
                <span style={{ color: P.muted }}>/ {totalSignals}</span>
              </div>
            )}
            {child && tab !== "screen" && (
              <div style={{ fontSize: 11, color: P.muted, padding: "0 4px" }}>
                <span style={{ fontWeight: 700, color: child.color || P.sky }}>{child.name}</span>
              </div>
            )}
          </div>

          {/* ── no child selected ── */}
          {!childId ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column", color: P.muted, textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 48, opacity: .3, marginBottom: 12 }}>👈</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: P.navy, marginBottom: 6 }}>Select a child</div>
              <div style={{ fontSize: 12, lineHeight: 1.8, maxWidth: 360 }}>
                Choose a child from the sidebar to view their screening history or start a new Quick Screener.
              </div>
            </div>
          ) : (

          <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

            {/* ══ OVERVIEW TAB ══ */}
            {tab === "overview" && (
              <div style={{ padding: 22, maxWidth: 900, margin: "0 auto" }}>

                {/* Child hero */}
                <div style={{ background: "white", borderRadius: 14, padding: "20px 24px",
                  marginBottom: 20, boxShadow: "0 2px 12px rgba(0,0,0,.07)",
                  display: "flex", alignItems: "center", gap: 18,
                  borderLeft: `5px solid ${child?.color || P.sky}` }}>
                  <div style={{ width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                    background: child?.color || P.sky,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", fontWeight: 900, fontSize: 24 }}>
                    {child?.name[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 900, fontSize: 22, color: P.navy,
                      fontFamily: "Lora, serif" }}>{child?.name}</div>
                    <div style={{ fontSize: 12, color: P.muted, marginTop: 3 }}>
                      {calcAge(child?.dob) != null ? `Age ${calcAge(child.dob)}` : ""}
                      {(child?.disabilities || []).length > 0 ? ` · ${child.disabilities.join(", ")}` : " · No disability noted"}
                    </div>
                  </div>
                  <button onClick={() => { startNewScreening(); setTab("screen"); }}
                    style={{ background: `linear-gradient(135deg,${P.navy},#1A3A5C)`, color: "white",
                      border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 13, fontWeight: 800,
                      cursor: "pointer", fontFamily: "Outfit, sans-serif", flexShrink: 0 }}>
                    ⚡ New Screening
                  </button>
                </div>

                {loadingHist && (
                  <div style={{ textAlign: "center", padding: 40, color: P.muted }}>
                    <span className="qs-spin">⏳</span> Loading…
                  </div>
                )}

                {!loadingHist && qsHistory.length === 0 && (
                  <div style={{ background: "white", borderRadius: 14, padding: 40,
                    textAlign: "center", color: P.muted, boxShadow: "0 1px 6px rgba(0,0,0,.05)" }}>
                    <div style={{ fontSize: 36, marginBottom: 10 }}>⚡</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: P.navy, marginBottom: 6 }}>No screenings yet</div>
                    <div style={{ fontSize: 12, marginBottom: 18, lineHeight: 1.8 }}>
                      {child?.name} has not been quick-screened yet.<br />
                      Go to the <strong>Screen</strong> tab to conduct the first 10-activity session.
                    </div>
                    <button onClick={() => setTab("screen")}
                      style={{ background: `linear-gradient(135deg,${P.navy},#1A3A5C)`, color: "white",
                        border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 800,
                        cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                      ⚡ Start First Screening
                    </button>
                  </div>
                )}

                {!loadingHist && qsHistory.length > 0 && activeResult && (
                  <ResultContent result={activeResult} forChild={child} forDate={activeResult.date?.split("T")[0]} />
                )}
              </div>
            )}

            {/* ══ HISTORY TAB ══ */}
            {tab === "history" && (
              <div style={{ padding: 22, maxWidth: 860, margin: "0 auto" }}>
                {loadingHist ? (
                  <div style={{ textAlign: "center", padding: 40, color: P.muted }}>
                    <span className="qs-spin">⏳</span> Loading…
                  </div>
                ) : qsHistory.length === 0 ? (
                  <div style={{ background: "white", borderRadius: 14, padding: 40,
                    textAlign: "center", color: P.muted, boxShadow: "0 1px 6px rgba(0,0,0,.05)" }}>
                    No past screenings for {child?.name} yet.
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {qsHistory.map(r => {
                      const fl = r.recommendation === "full-srt" ? "red" : r.recommendation === "monitor" ? "amber" : "green";
                      const fc = flagColor(fl); const fb = flagBg(fl);
                      const isActive = activeResult?._id === r._id;
                      return (
                        <div key={r._id} onClick={() => setActiveResult(r)}
                          style={{ background: "white", borderRadius: 12, padding: "14px 18px",
                            border: isActive ? `2px solid ${fc}` : `1.5px solid ${P.border}`,
                            cursor: "pointer", display: "flex", alignItems: "center", gap: 14,
                            boxShadow: isActive ? `0 4px 16px ${fc}22` : "0 1px 5px rgba(0,0,0,.04)" }}>
                          <div style={{ width: 44, height: 44, borderRadius: 11, background: fb,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 20, flexShrink: 0 }}>
                            {fl === "green" ? "✅" : fl === "amber" ? "⚠️" : "🔴"}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 800, fontSize: 13, color: P.ink }}>
                              {new Date(r.date || r.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                            </div>
                            <div style={{ fontSize: 11, color: P.muted, marginTop: 2 }}>
                              {r.recommendation === "full-srt" ? "Full SRT Recommended" : r.recommendation === "monitor" ? "Monitor & Support" : "No Concerns"}
                            </div>
                          </div>
                          <div style={{ fontSize: 22, fontWeight: 900, color: fc, fontFamily: "Lora, serif" }}>
                            {r.overallScore}%
                          </div>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button onClick={e => { e.stopPropagation(); loadQSResult(r); }}
                              style={{ background: P.fog, color: P.muted, border: `1px solid ${P.border}`,
                                borderRadius: 7, padding: "5px 10px", fontSize: 10, cursor: "pointer",
                                fontFamily: "Outfit, sans-serif", fontWeight: 700 }}>
                              ✏️ Edit
                            </button>
                            <button onClick={e => { e.stopPropagation(); generateQSReport({ ...r, date: r.date?.split("T")[0]||date }, child||{}); }}
                              style={{ background: P.navy, color: "white", border: "none",
                                borderRadius: 7, padding: "5px 10px", fontSize: 10, cursor: "pointer",
                                fontFamily: "Outfit, sans-serif", fontWeight: 700 }}>
                              🖨️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {activeResult && (
                  <div style={{ marginTop: 20 }}>
                    <ResultContent result={activeResult} forChild={child} forDate={activeResult.date?.split("T")[0]} />
                  </div>
                )}
              </div>
            )}

            {/* ══ SCREEN TAB ══ */}
            {tab === "screen" && (
              <div style={{ padding: 20, maxWidth: 880, margin: "0 auto", paddingBottom: 64 }}>

                {/* Child + session header */}
                <div style={{ background: "white", borderRadius: 12, padding: "12px 16px", marginBottom: 14,
                  display: "flex", alignItems: "center", gap: 10,
                  boxShadow: "0 1px 6px rgba(0,0,0,.06)", border: `1.5px solid ${P.border}` }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, background: child.color || P.sky,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", fontWeight: 900, fontSize: 16, flexShrink: 0 }}>
                    {child.name[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{child.name}</div>
                    <div style={{ fontSize: 10, color: P.muted, marginTop: 1 }}>
                      {calcAge(child.dob) != null ? `Age ${calcAge(child.dob)} · ` : ""}{(child.disabilities || []).join(", ") || "No disability noted"}
                    </div>
                  </div>
                  {currentScreenerId && <span style={{ fontSize: 10, background: "#E8F0FE", color: P.sky, padding: "2px 8px", borderRadius: 6, fontWeight: 700 }}>📌 saved</span>}
                  <button onClick={() => setShowSlideshow(true)}
                    style={{ background: "#FFFBEA", color: "#B08000", border: "1.5px solid #E8C84A",
                      borderRadius: 7, padding: "5px 12px", fontSize: 11, cursor: "pointer",
                      fontFamily: "Outfit, sans-serif", fontWeight: 700 }}>
                    🎬 Exam Mode
                  </button>
                  <button onClick={startNewScreening}
                    style={{ background: P.fog, color: P.muted, border: `1.5px solid ${P.border}`,
                      borderRadius: 7, padding: "5px 10px", fontSize: 10, cursor: "pointer",
                      fontFamily: "Outfit, sans-serif", fontWeight: 700 }}>
                    + New
                  </button>
                </div>

                {/* Live result preview */}
                {liveResult && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginBottom: 16 }}>
                    {DOMAINS.map(d => (
                      <DomainBadge key={d.id} domain={d} score={liveResult.domainScores[d.id]} flag={liveResult.flags[d.id]} />
                    ))}
                  </div>
                )}

                {/* Activities list */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {ACTIVITIES.map(act => (
                    <ActivityCard key={act.id} activity={act} ratings={ratings}
                      onRate={rateSignal}
                      isActive={activeAct === act.id}
                      onToggle={() => setActiveAct(activeAct === act.id ? null : act.id)}
                      completed={actComplete(act)} />
                  ))}
                </div>

                {/* Save footer */}
                <div style={{ position: "sticky", bottom: 0, background: "white",
                  border: `1.5px solid ${P.border}`, borderRadius: 12, padding: "10px 16px", marginTop: 14,
                  display: "flex", alignItems: "center", gap: 10, boxShadow: "0 -4px 20px rgba(0,0,0,.07)" }}>
                  <span style={{ fontSize: 11, color: P.muted }}>{completedCount}/10 activities · {ratedSignals}/{totalSignals} signals</span>
                  <div style={{ flex: 1 }} />
                  <button onClick={saveScreening} disabled={saving || ratedSignals === 0} style={{
                    background: (saving || ratedSignals === 0) ? "#eee" : "#F5D000",
                    color: (saving || ratedSignals === 0) ? P.muted : P.navy,
                    border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 800,
                    fontSize: 12, cursor: (saving || ratedSignals === 0) ? "not-allowed" : "pointer", fontFamily: "Outfit, sans-serif" }}>
                    {saving ? "Saving…" : currentScreenerId ? "💾 Update Screening" : "💾 Save Screening"}
                  </button>
                  {liveResult && (
                    <button onClick={() => generateQSReport({ ...liveResult, overallScore: liveResult.overall, ratings, date, notes:null }, child || {})}
                      style={{ background: P.navy, color: "white", border: "none", borderRadius: 8,
                        padding: "8px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer",
                        fontFamily: "Outfit, sans-serif" }}>🖨️ Print</button>
                  )}
                </div>
              </div>
            )}

          </div>
          )}
        </div>
      </div>
    </div>
  );
}